import { PREVIEW_PLACEMENT, SIZE_PRESETS } from './mm-tote-artwork-config.js';
import { renderCustomizerOptions, setSelectedOption } from './mm-tote-customizer-widget.js';

const PERSONALIZATION_PATTERN = /^[A-Za-z '\-]+$/;

export function normalizeText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

export function validatePersonalization(value, maxLength = 12) {
  const normalized = normalizeText(value);
  if (!normalized) {
    return { valid: false, value: normalized, error: 'Enter the name or word you want embroidered.' };
  }
  if (normalized.length > maxLength) {
    return { valid: false, value: normalized, error: `Use ${maxLength} characters or fewer.` };
  }
  if (!PERSONALIZATION_PATTERN.test(normalized)) {
    return { valid: false, value: normalized, error: 'Use English letters, spaces, apostrophes or hyphens only.' };
  }
  return { valid: true, value: normalized, error: '' };
}

export function serializeProperties(state) {
  return {
    'Personalized text': normalizeText(state.text),
    'Font style': `${state.fontCode} ${state.fontName}`.trim(),
    'Embroidery size': state.sizeName || 'Small',
    'Thread color': `#${state.threadNumber} ${state.threadName} (${state.threadCode})`
  };
}

export async function loadPreviewFont(button, sample = 'Sylvia') {
  const family = button?.dataset.fontFamily;
  if (!family || typeof document === 'undefined' || !document.fonts?.load) return;
  await document.fonts.load(`1em "${family}"`, sample);
}

export function applyPreviewPlacement(element, root = null, placement = PREVIEW_PLACEMENT) {
  if (!element) return;
  const placementTarget = root?.style ? root : element;
  if (!placementTarget.style.getPropertyValue?.('--mm-preview-x').trim()) {
    placementTarget.style.setProperty('--mm-preview-x', `${placement.xPercent}%`);
  }
  if (!placementTarget.style.getPropertyValue?.('--mm-preview-y').trim()) {
    placementTarget.style.setProperty('--mm-preview-y', `${placement.yPercent}%`);
  }
  element.style.setProperty('--mm-preview-font-size', `${placement.mediumFontSizeCqw}cqw`);
  element.style.width = `${placement.maxWidthPercent}%`;
}

function fitPreviewText(element, length) {
  element.dataset.length = length > 10 ? 'long' : length > 7 ? 'medium' : 'short';
}

export function initPersonalizedTote(root) {
  if (!root || root.dataset.mmReady === 'true') return;
  root.dataset.mmReady = 'true';

  renderCustomizerOptions({
    fontRoot: root.querySelector('[data-mm-font-options]'),
    sizeRoot: root.querySelector('[data-mm-size-options]'),
    threadRoot: root.querySelector('[data-mm-thread-options]'),
    context: 'storefront'
  });

  const form = root.querySelector('[data-mm-form], .mm-tote__form');
  const textInput = root.querySelector('[data-mm-text-input]');
  const textError = root.querySelector('[data-mm-text-error]');
  const counter = root.querySelector('[data-mm-count]');
  const previewText = root.querySelector('[data-mm-preview-text]');
  const previewImage = root.querySelector('[data-mm-preview-image]');
  const summary = root.querySelector('[data-mm-summary]');
  const liveRegion = root.querySelector('[data-mm-live]');
  const threadLabel = root.querySelector('[data-mm-thread-label]');
  const variantInput = form?.querySelector('input[name="id"]');
  const fontProperty = form?.querySelector('input[name="properties[Font style]"]');
  const sizeProperty = form?.querySelector('input[name="properties[Embroidery size]"]');
  const threadProperty = form?.querySelector('input[name="properties[Thread color]"]');
  const textProperty = form?.querySelector('input[name="properties[Personalized text]"]');
  const submitButtons = [...root.querySelectorAll('[data-mm-submit]')];
  const colorButtons = [...root.querySelectorAll('[data-mm-color]')];
  const fontButtons = [...root.querySelectorAll('[data-mm-font]')];
  const sizeButtons = [...root.querySelectorAll('[data-mm-size]')];
  const threadButtons = [...root.querySelectorAll('[data-mm-thread]')];

  applyPreviewPlacement(previewText, root);

  const activeColor = colorButtons.find((button) => button.classList.contains('is-selected')) || colorButtons[0];
  const activeFont = fontButtons.find((button) => button.classList.contains('is-selected')) || fontButtons[0];
  const activeSize = sizeButtons.find((button) => button.classList.contains('is-selected')) || sizeButtons[0];
  const activeThread = threadButtons.find((button) => button.classList.contains('is-selected')) || threadButtons[0];
  const defaultSize = SIZE_PRESETS.find((size) => size.default) || SIZE_PRESETS[0];
  const state = {
    colorName: activeColor?.dataset.name || '',
    variantId: activeColor?.dataset.variantId || '',
    available: activeColor?.dataset.available !== 'false',
    text: textInput?.value || 'EMMA',
    fontCode: activeFont?.dataset.code || 'F1',
    fontName: activeFont?.dataset.name || 'Varsity Block',
    fontClass: activeFont?.dataset.fontClass || 'mm-font-varsity-block',
    sizeCode: activeSize?.dataset.code || defaultSize.code,
    sizeName: activeSize?.dataset.name || defaultSize.label,
    sizeScale: Number(activeSize?.dataset.scale || defaultSize.scale),
    threadNumber: activeThread?.dataset.number || '09',
    threadName: activeThread?.dataset.name || 'Warm Beige',
    threadCode: activeThread?.dataset.code || '6746',
    threadHex: activeThread?.dataset.hex || '#CBB892'
  };
  let fontLoadRequest = 0;

  function update() {
    const validation = validatePersonalization(textInput?.value, Number(textInput?.maxLength || 12));
    state.text = validation.value;
    if (counter) counter.textContent = String(textInput?.value.length || 0);
    if (textError) textError.textContent = validation.error;
    textInput?.setAttribute('aria-invalid', String(!validation.valid));
    if (previewText) {
      previewText.textContent = validation.value || 'Your text';
      previewText.dataset.previewValue = validation.value || 'Your text';
      previewText.className = `mm-tote__preview-text ${state.fontClass}`;
      previewText.dataset.size = state.sizeCode;
      previewText.style.color = state.threadHex;
      previewText.style.setProperty('--mm-thread-color', state.threadHex);
      fitPreviewText(previewText, validation.value.length);
    }
    if (variantInput) variantInput.value = state.variantId;
    const properties = serializeProperties(state);
    if (textProperty) textProperty.value = properties['Personalized text'];
    if (fontProperty) fontProperty.value = properties['Font style'];
    if (sizeProperty) sizeProperty.value = properties['Embroidery size'];
    if (threadProperty) threadProperty.value = properties['Thread color'];
    if (summary) {
      summary.textContent = `${state.colorName} · “${validation.value || 'Your text'}” · ${state.fontCode} ${state.fontName} · ${state.sizeName} · #${state.threadNumber} ${state.threadName}`;
    }
    submitButtons.forEach((button) => {
      button.disabled = !validation.valid || !state.available;
      button.dataset.defaultLabel = state.available ? (button.dataset.mmLabel || 'Add your personalized bag') : 'This color is unavailable';
      button.textContent = button.dataset.defaultLabel;
    });
    return validation;
  }

  textInput?.addEventListener('input', update);
  textInput?.addEventListener('blur', () => {
    const validation = update();
    if (validation.valid) textInput.value = validation.value;
  });

  colorButtons.forEach((button) => {
    button.addEventListener('click', () => {
      setSelectedOption(colorButtons, button);
      state.colorName = button.dataset.name || '';
      state.variantId = button.dataset.variantId || '';
      state.available = button.dataset.available !== 'false';
      if (previewImage && button.dataset.preview) {
        previewImage.src = button.dataset.preview;
        previewImage.alt = `${state.colorName} corduroy tote live personalization preview`;
      }
      update();
      if (liveRegion) liveRegion.textContent = `${state.colorName} bag selected.`;
    });
  });

  fontButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const request = ++fontLoadRequest;
      button.setAttribute('aria-busy', 'true');
      try {
        await loadPreviewFont(button, normalizeText(textInput?.value) || 'Sylvia');
        if (request !== fontLoadRequest) return;
        setSelectedOption(fontButtons, button);
        state.fontCode = button.dataset.code || '';
        state.fontName = button.dataset.name || '';
        state.fontClass = button.dataset.fontClass || '';
        update();
        if (liveRegion) liveRegion.textContent = `${state.fontCode} ${state.fontName} selected.`;
      } catch {
        if (liveRegion) liveRegion.textContent = `${button.dataset.code || 'This'} font style could not load. Your previous style is still selected.`;
      } finally {
        button.removeAttribute('aria-busy');
      }
    });
  });

  sizeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      setSelectedOption(sizeButtons, button);
      state.sizeCode = button.dataset.code || defaultSize.code;
      state.sizeName = button.dataset.name || defaultSize.label;
      state.sizeScale = Number(button.dataset.scale || defaultSize.scale);
      update();
      if (liveRegion) liveRegion.textContent = `${state.sizeName} embroidery size selected.`;
    });
  });

  threadButtons.forEach((button) => {
    button.addEventListener('click', () => {
      setSelectedOption(threadButtons, button);
      state.threadNumber = button.dataset.number || '';
      state.threadName = button.dataset.name || '';
      state.threadCode = button.dataset.code || '';
      state.threadHex = button.dataset.hex || '#CBB892';
      update();
      if (threadLabel) {
        threadLabel.textContent = `#${state.threadNumber} ${state.threadName} (${state.threadCode})`;
      }
      if (liveRegion) liveRegion.textContent = `Thread color ${state.threadName} selected.`;
    });
  });

  root.querySelectorAll('[data-mm-gallery-thumb]').forEach((button) => {
    button.addEventListener('click', () => {
      const panels = [...root.querySelectorAll('[data-mm-gallery-panel]')];
      const thumbs = [...root.querySelectorAll('[data-mm-gallery-thumb]')];
      panels.forEach((panel) => { panel.hidden = panel.id !== button.getAttribute('aria-controls'); });
      thumbs.forEach((thumb) => thumb.setAttribute('aria-selected', String(thumb === button)));
    });
  });

  form?.addEventListener('submit', async (event) => {
    const validation = update();
    if (!validation.valid || !state.available) {
      event.preventDefault();
      textInput?.focus();
      return;
    }
    if (!window.fetch || form.dataset.ajax === 'false') return;
    event.preventDefault();
    submitButtons.forEach((button) => {
      button.disabled = true;
      button.textContent = 'Adding your bag…';
    });
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'Shopify could not add this bag to the cart.');
      submitButtons.forEach((button) => { button.textContent = 'Added to cart'; });
      if (liveRegion) liveRegion.textContent = `${validation.value} personalized bag added to cart.`;
      document.dispatchEvent(new CustomEvent('mm:cart:add', { detail: payload }));
      setTimeout(() => update(), 1800);
    } catch (error) {
      submitButtons.forEach((button) => {
        button.disabled = false;
        button.textContent = button.dataset.mmRetryLabel || 'Try adding again';
      });
      if (liveRegion) liveRegion.textContent = error.message;
    }
  });

  const mainSubmit = root.querySelector('[data-mm-main-submit]');
  const stickyBar = root.querySelector('[data-mm-sticky-bar]');
  if (mainSubmit && stickyBar && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      stickyBar.hidden = entry.isIntersecting;
    }, { threshold: 0.15 });
    observer.observe(mainSubmit);
  }

  update();
}

if (typeof document !== 'undefined') {
  const init = () => document.querySelectorAll('[data-mm-personalized-tote]').forEach(initPersonalizedTote);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
}
